import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import FaceRetouchingNaturalRoundedIcon from '@mui/icons-material/FaceRetouchingNaturalRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import SmartphoneRoundedIcon from '@mui/icons-material/SmartphoneRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import SubscriptionPrompt from '../Shared/SubscriptionPrompt';
import './ProfileCompletenessCard.css';

const ProfileCompletenessCard = () => {
    const navigate = useNavigate();
    const userDetails = useSelector((state) => state.userDetails?.userDetails);

    const [isExpanded, setIsExpanded] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalType, setModalType] = useState('face-recognition'); // 'face-recognition' | 'government-id'

    const openModal = (type) => {
        setModalType(type);
        setModalOpen(true);
    };

    const closeModal = () => setModalOpen(false);

    // 1. Basic details completion
    const profileDetailsStatus = useMemo(() => {
        if (!userDetails) return { isComplete: false, missingFields: [] };

        const missing = [];
        if (!userDetails.firstName || userDetails.firstName.trim() === '') missing.push('First Name');
        if (!userDetails.lastName || userDetails.lastName.trim() === '') missing.push('Last Name');
        if (!userDetails.dateOfBirth) missing.push('Date of Birth');
        if (!userDetails.gender || userDetails.gender.trim() === '') missing.push('Gender');
        if (!userDetails.languages || userDetails.languages.length === 0) missing.push('Languages');
        if (!userDetails.addresses || userDetails.addresses.length === 0) missing.push('Address');
        if (!userDetails.photos || userDetails.photos.trim() === '') missing.push('Cover Photo');

        return {
            isComplete: missing.length === 0,
            missingFields: missing,
        };
    }, [userDetails]);

    // 2. Face recognition completion
    const faceRecognitionStatus = useMemo(() => {
        if (!userDetails) return { isComplete: false };
        const isComplete = Boolean(
            userDetails.faceId &&
            userDetails.faceId !== null &&
            userDetails.faceId !== '' &&
            userDetails.faceId.trim() !== ''
        );
        return { isComplete };
    }, [userDetails]);

    // 3. Government ID completion
    const governmentIdStatus = useMemo(() => {
        if (!userDetails?.governmentId) return { isComplete: false, isUploaded: false, isVerified: false };
        const isUploaded = Boolean(userDetails.governmentId.id && userDetails.governmentId.id !== null);
        const isVerified = Boolean(userDetails.governmentId.isVerified);
        return {
            isComplete: isUploaded && isVerified,
            isUploaded,
            isVerified,
        };
    }, [userDetails]);

    // Calculate percentage based on 3 milestones
    const completedCount = useMemo(() => {
        let count = 0;
        if (profileDetailsStatus.isComplete) count += 1;
        if (faceRecognitionStatus.isComplete) count += 1;
        if (governmentIdStatus.isComplete) count += 1;
        return count;
    }, [profileDetailsStatus.isComplete, faceRecognitionStatus.isComplete, governmentIdStatus.isComplete]);

    const percentage = Math.round((completedCount / 3) * 100);
    const isFullyComplete = completedCount === 3;

    const handleScrollToDetails = (e) => {
        e?.stopPropagation();
        const target = document.querySelector('.imageContainer');
        if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div className={`pccContainer ${isExpanded ? 'is-expanded' : ''}`}>
            {/* Collapsed Bar: Title + Status Icons + Progress Badge + Expand Toggle */}
            <div
                className="pccBarHeader"
                onClick={() => setIsExpanded(!isExpanded)}
                role="button"
                tabIndex={0}
                aria-expanded={isExpanded}
            >
                <div className="pccBarLeft">
                    <div className={`pccIconBadge ${isFullyComplete ? 'complete' : ''}`}>
                        <VerifiedUserRoundedIcon sx={{ fontSize: 18 }} />
                    </div>
                    <span className="pccTitle">Profile Completeness</span>
                </div>

                {/* Milestone Status Icons (shows at a glance what is completed and what is not) */}
                <div className="pccMilestoneIcons">
                    {/* Basic Info Icon Chip */}
                    <div
                        className={`pccIconChip ${profileDetailsStatus.isComplete ? 'chip-success' : 'chip-warning'}`}
                        title={
                            profileDetailsStatus.isComplete
                                ? 'Basic Information: Complete'
                                : `Basic Information: Missing ${profileDetailsStatus.missingFields.join(', ')}`
                        }
                    >
                        <PersonRoundedIcon sx={{ fontSize: 15 }} />
                        {profileDetailsStatus.isComplete ? (
                            <CheckCircleRoundedIcon className="pccMiniBadge" sx={{ fontSize: 12 }} />
                        ) : (
                            <WarningAmberRoundedIcon className="pccMiniBadge" sx={{ fontSize: 12 }} />
                        )}
                    </div>

                    {/* Face Recognition Icon Chip */}
                    <div
                        className={`pccIconChip ${faceRecognitionStatus.isComplete ? 'chip-success' : 'chip-pending'}`}
                        title={
                            faceRecognitionStatus.isComplete
                                ? 'Face Recognition: Registered'
                                : 'Face Recognition: Not Registered'
                        }
                    >
                        <FaceRetouchingNaturalRoundedIcon sx={{ fontSize: 15 }} />
                        {faceRecognitionStatus.isComplete ? (
                            <CheckCircleRoundedIcon className="pccMiniBadge" sx={{ fontSize: 12 }} />
                        ) : (
                            <SmartphoneRoundedIcon className="pccMiniBadge" sx={{ fontSize: 12 }} />
                        )}
                    </div>

                    {/* Government ID Icon Chip */}
                    <div
                        className={`pccIconChip ${
                            governmentIdStatus.isComplete
                                ? 'chip-success'
                                : governmentIdStatus.isUploaded
                                ? 'chip-pending'
                                : 'chip-warning'
                        }`}
                        title={
                            governmentIdStatus.isComplete
                                ? 'Government ID: Verified'
                                : governmentIdStatus.isUploaded
                                ? 'Government ID: Pending Review'
                                : 'Government ID: Not Uploaded'
                        }
                    >
                        <VerifiedUserRoundedIcon sx={{ fontSize: 15 }} />
                        {governmentIdStatus.isComplete ? (
                            <CheckCircleRoundedIcon className="pccMiniBadge" sx={{ fontSize: 12 }} />
                        ) : governmentIdStatus.isUploaded ? (
                            <HourglassEmptyRoundedIcon className="pccMiniBadge" sx={{ fontSize: 12 }} />
                        ) : (
                            <WarningAmberRoundedIcon className="pccMiniBadge" sx={{ fontSize: 12 }} />
                        )}
                    </div>
                </div>

                {/* Right side: Overall status badge + Dropdown expand button */}
                <div className="pccBarRight">
                    <span className={`pccPercentageBadge ${isFullyComplete ? 'complete' : ''}`}>
                        {isFullyComplete ? (
                            <>
                                <CheckCircleRoundedIcon sx={{ fontSize: 13, mr: 0.5 }} />
                                100% Fully Verified
                            </>
                        ) : (
                            `${percentage}% Complete (${completedCount}/3)`
                        )}
                    </span>

                    <button
                        type="button"
                        className={`pccExpandBtn ${isExpanded ? 'rotated' : ''}`}
                        aria-label={isExpanded ? 'Collapse profile details' : 'Expand profile details'}
                    >
                        <KeyboardArrowDownRoundedIcon sx={{ fontSize: 20 }} />
                    </button>
                </div>
            </div>

            {/* Expandable Section: Full Progress Bar + Detailed List of Steps */}
            {isExpanded && (
                <div className="pccExpandedDrawer">
                    <div className="pccDrawerDivider" />

                    {/* Progress Bar with Percentage */}
                    <div className="pccProgressContainer">
                        <div className="pccProgressMeta">
                            <span className="pccProgressText">Overall Verification Progress</span>
                            <span className="pccProgressPercent">{percentage}% Completed</span>
                        </div>
                        <div className="pccProgressTrack">
                            <div
                                className={`pccProgressBar ${isFullyComplete ? 'complete' : ''}`}
                                style={{ width: `${percentage}%` }}
                            />
                        </div>
                    </div>

                    {/* Full Detailed Steps List */}
                    <div className="pccStepsList">
                        {/* 1. Basic Information Row */}
                        <div className={`pccStepRow ${profileDetailsStatus.isComplete ? 'is-complete' : 'is-warning'}`}>
                            <div className="pccStepRowLeft">
                                <div className="pccStepIconWrap">
                                    <PersonRoundedIcon sx={{ fontSize: 18 }} />
                                </div>
                                <div className="pccStepContent">
                                    <div className="pccStepTitle">Basic Information</div>
                                    <div className="pccStepSubtitle">
                                        {profileDetailsStatus.isComplete ? (
                                            'All personal details, address, and cover photo are filled.'
                                        ) : (
                                            <span className="pccMissingText">
                                                Missing: {profileDetailsStatus.missingFields.join(', ')}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="pccStepRowRight">
                                <span className={`pccStepBadge ${profileDetailsStatus.isComplete ? 'success' : 'warning'}`}>
                                    {profileDetailsStatus.isComplete ? (
                                        <>
                                            <CheckCircleRoundedIcon sx={{ fontSize: 13 }} /> Completed
                                        </>
                                    ) : (
                                        <>
                                            <WarningAmberRoundedIcon sx={{ fontSize: 13 }} /> Incomplete
                                        </>
                                    )}
                                </span>
                                <button
                                    type="button"
                                    className="pccStepActionBtn"
                                    onClick={handleScrollToDetails}
                                >
                                    {profileDetailsStatus.isComplete ? 'View Details' : 'Complete Now'} &darr;
                                </button>
                            </div>
                        </div>

                        {/* 2. Face Recognition Row */}
                        <div className={`pccStepRow ${faceRecognitionStatus.isComplete ? 'is-complete' : 'is-actionable'}`}>
                            <div className="pccStepRowLeft">
                                <div className="pccStepIconWrap">
                                    <FaceRetouchingNaturalRoundedIcon sx={{ fontSize: 18 }} />
                                </div>
                                <div className="pccStepContent">
                                    <div className="pccStepTitle">Face Recognition</div>
                                    <div className="pccStepSubtitle">
                                        {faceRecognitionStatus.isComplete
                                            ? 'Biometric face registration is active for secure authentication.'
                                            : 'Enable face recognition via the Amalgamate mobile app.'}
                                    </div>
                                </div>
                            </div>
                            <div className="pccStepRowRight">
                                <span className={`pccStepBadge ${faceRecognitionStatus.isComplete ? 'success' : 'pending'}`}>
                                    {faceRecognitionStatus.isComplete ? (
                                        <>
                                            <CheckCircleRoundedIcon sx={{ fontSize: 13 }} /> Registered
                                        </>
                                    ) : (
                                        <>
                                            <SmartphoneRoundedIcon sx={{ fontSize: 13 }} /> Not Registered
                                        </>
                                    )}
                                </span>
                                <button
                                    type="button"
                                    className="pccStepActionBtn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (faceRecognitionStatus.isComplete) {
                                            navigate('/face-recognition');
                                        } else {
                                            openModal('face-recognition');
                                        }
                                    }}
                                >
                                    {faceRecognitionStatus.isComplete ? (
                                        'Manage Face ID'
                                    ) : (
                                        <>
                                            <SmartphoneRoundedIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                            Verify on App
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* 3. Government ID Row */}
                        <div
                            className={`pccStepRow ${
                                governmentIdStatus.isComplete
                                    ? 'is-complete'
                                    : governmentIdStatus.isUploaded
                                    ? 'is-pending'
                                    : 'is-actionable'
                            }`}
                        >
                            <div className="pccStepRowLeft">
                                <div className="pccStepIconWrap">
                                    <VerifiedUserRoundedIcon sx={{ fontSize: 18 }} />
                                </div>
                                <div className="pccStepContent">
                                    <div className="pccStepTitle">Government ID</div>
                                    <div className="pccStepSubtitle">
                                        {governmentIdStatus.isComplete
                                            ? 'Official government identification is verified and approved.'
                                            : governmentIdStatus.isUploaded
                                            ? 'Document submitted and undergoing verification review.'
                                            : 'Upload Passport, Driving License, or Aadhaar via the mobile app.'}
                                    </div>
                                </div>
                            </div>
                            <div className="pccStepRowRight">
                                <span
                                    className={`pccStepBadge ${
                                        governmentIdStatus.isComplete
                                            ? 'success'
                                            : governmentIdStatus.isUploaded
                                            ? 'pending'
                                            : 'warning'
                                    }`}
                                >
                                    {governmentIdStatus.isComplete ? (
                                        <>
                                            <CheckCircleRoundedIcon sx={{ fontSize: 13 }} /> Verified
                                        </>
                                    ) : governmentIdStatus.isUploaded ? (
                                        <>
                                            <HourglassEmptyRoundedIcon sx={{ fontSize: 13 }} /> Pending Approval
                                        </>
                                    ) : (
                                        <>
                                            <WarningAmberRoundedIcon sx={{ fontSize: 13 }} /> Not Uploaded
                                        </>
                                    )}
                                </span>
                                <button
                                    type="button"
                                    className="pccStepActionBtn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openModal('government-id');
                                    }}
                                >
                                    {governmentIdStatus.isComplete ? (
                                        'Verified Status'
                                    ) : (
                                        <>
                                            <SmartphoneRoundedIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                            {governmentIdStatus.isUploaded ? 'Check Status' : 'Upload on App'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Mobile Verification Prompt Modal */}
            <SubscriptionPrompt
                open={modalOpen}
                onClose={closeModal}
                title={modalType === 'government-id' ? 'Verify Government ID on Mobile App' : 'Complete Face ID on Mobile App'}
                message={
                    modalType === 'government-id'
                        ? 'For security and optical verification, Government ID upload must be completed using the Amalgamate mobile app on iOS or Android.'
                        : 'Face recognition requires native biometric camera capture. Please launch the Amalgamate mobile app on your smartphone to register your face.'
                }
                primaryAction={{ label: 'Understood', onClick: closeModal, color: '#3BB3AE' }}
            />
        </div>
    );
};

export default ProfileCompletenessCard;
